import { useEffect, useRef } from 'react';

/**
 * Auto-loads the next infinite-scroll page when content does not overflow the
 * viewport. react-infinite-scroll-component only loads on scroll events, so
 * short layouts (common at some responsive breakpoints) never trigger `next`.
 */
export default function useFillViewportInfiniteScroll({
  hasMore,
  onLoadMore,
  loadedCount,
}) {
  const onLoadMoreRef = useRef(onLoadMore);
  const pendingRef = useRef(false);
  const loadedCountRef = useRef(loadedCount);
  const hasMoreRef = useRef(hasMore);
  const rafRef = useRef(null);

  onLoadMoreRef.current = onLoadMore;
  hasMoreRef.current = hasMore;

  useEffect(() => {
    if (loadedCount !== loadedCountRef.current) {
      pendingRef.current = false;
      loadedCountRef.current = loadedCount;
    }
  }, [loadedCount]);

  useEffect(() => {
    if (!hasMore) {
      pendingRef.current = false;
      return undefined;
    }

    const tryLoadMore = () => {
      if (!hasMoreRef.current || pendingRef.current) return;

      const { scrollHeight, clientHeight } = document.documentElement;
      if (scrollHeight <= clientHeight + 8) {
        pendingRef.current = true;
        onLoadMoreRef.current();
      }
    };

    // Defer so ResizeObserver never mutates layout in the same frame
    // (avoids "ResizeObserver loop completed with undelivered notifications").
    const scheduleTryLoadMore = () => {
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        tryLoadMore();
      });
    };

    scheduleTryLoadMore();
    window.addEventListener('resize', scheduleTryLoadMore);

    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(scheduleTryLoadMore);
      observer.observe(document.body);
    }

    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      window.removeEventListener('resize', scheduleTryLoadMore);
      observer?.disconnect();
    };
  }, [hasMore, loadedCount]);
}
